import { useState, useEffect } from 'react';

const LAST_UPDATED_DATE = 'March 5, 2026';
const LAST_UPDATED_KEY = 'jobos_terms_last_seen_date';

/* ── COLLAPSIBLE SECTION WRAPPER ── */
function CollapsibleSection({ id, num, title, children }: {
  id: string;
  num: string;
  title: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(true);

  return (
    <section id={id} className="mt-12 pt-4">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 mb-0 pb-4 border-b border-gray-100 dark:border-gray-800 group cursor-pointer"
      >
        <span className="text-xs font-black text-[#6366F1] tracking-widest font-mono flex-shrink-0">{num}</span>
        <div className="h-px flex-1 bg-gray-100 dark:bg-gray-800"></div>
        <h2 className="text-lg font-extrabold uppercase tracking-[0.12em] text-gray-700 dark:text-gray-300 text-right group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
          {title}
        </h2>
        <span
          className={`w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 group-hover:bg-[#6366F1]/10 transition-all flex-shrink-0 ${open ? 'rotate-180' : 'rotate-0'}`}
          style={{ transition: 'transform 0.3s ease, background 0.2s' }}
        >
          <i className="ri-arrow-down-s-line text-gray-400 group-hover:text-[#6366F1] text-base"></i>
        </span>
      </button>

      <div
        style={{
          maxHeight: open ? '9999px' : '0px',
          overflow: 'hidden',
          transition: open ? 'max-height 0.5s ease' : 'max-height 0.3s ease',
          opacity: open ? 1 : 0,
        }}
      >
        <div className="pt-6">
          {children}
        </div>
      </div>
    </section>
  );
}

/* ── SHARED SUB-COMPONENTS ── */

function SubSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-sm font-black text-gray-900 dark:text-white mb-4 uppercase tracking-wider">{title}</h3>
      {children}
    </div>
  );
}

function BulletList({ items }: { items: { label: string; desc: string }[] }) {
  return (
    <div className="space-y-4">
      {items.map((item, idx) => (
        <div key={idx} className="flex items-start gap-4 p-5 rounded-xl bg-gray-50 dark:bg-[#0D111A] border border-gray-200 dark:border-[#2D3148]">
          <div className="w-8 h-8 flex-shrink-0 rounded-lg bg-[#6366F1]/10 border border-[#6366F1]/20 flex items-center justify-center">
            <span className="text-[#6366F1] font-bold text-sm">({item.label})</span>
          </div>
          <div>
            <div className="font-bold text-gray-900 dark:text-white mb-1">{item.desc}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function DefinitionItem({ term, definition }: { term: string; definition: string }) {
  return (
    <div className="mb-4">
      <dt className="text-sm font-bold text-gray-900 dark:text-white mb-1.5">"{term}"</dt>
      <dd className="text-sm text-gray-600 dark:text-gray-400 pl-4">{definition}</dd>
    </div>
  );
}

export default function TermsContent() {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    definitions: true,
    operator: true,
    eligibility: true,
    description: true,
    acceptable: true,
    extension: true,
    subscription: true,
  });

  const [isNewUpdate, setIsNewUpdate] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  useEffect(() => {
    const lastSeen = localStorage.getItem(LAST_UPDATED_KEY);
    if (lastSeen !== LAST_UPDATED_DATE) {
      setIsNewUpdate(true);
    }
  }, []);

  const handleDismiss = () => {
    localStorage.setItem(LAST_UPDATED_KEY, LAST_UPDATED_DATE);
    setIsNewUpdate(false);
    setBannerDismissed(true);
  };

  const sections = [
    { id: 'definitions', label: '1. Definitions' },
    { id: 'operator', label: '2. Operator Identity' },
    { id: 'eligibility', label: '3. Eligibility & Registration' },
    { id: 'description', label: '4. Description of Service' },
    { id: 'acceptable', label: '5. Acceptable Use Policy' },
    { id: 'section-6', label: '6. Chrome Extension Terms' },
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0E1A]">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="flex gap-12">
          {/* ── SIDEBAR TOC ── */}
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-24 space-y-6">
              <nav className="bg-white dark:bg-[#141828]/50 rounded-2xl p-6 border border-gray-200 dark:border-white/10 backdrop-blur-md">
                <h3 className="text-xs font-black text-gray-900 dark:text-white mb-5 uppercase tracking-[0.2em] font-mono border-b border-gray-100 dark:border-white/5 pb-4">
                  Contents
                </h3>
                <ul className="space-y-1.5 text-sm">
                  {[
                    { label: 'Acceptance of Terms', id: 'section-1' },
                    { label: 'Definitions', id: 'section-2' },
                    { label: 'Eligibility', id: 'section-3' },
                    { label: 'Description of Service', id: 'section-4' },
                    { label: 'Acceptable Use Policy', id: 'section-5' },
                    { label: 'Chrome Extension Terms', id: 'section-6' },
                    { label: 'AI Features & Disclaimers', id: 'section-7' },
                    { label: 'Affiliate Programme', id: 'section-8' },
                    { label: 'Subscription & Payment', id: 'section-9' },
                    { label: 'Intellectual Property', id: 'section-10' },
                    { label: 'Third-Party Services', id: 'section-11' },
                    { label: 'Privacy', id: 'section-12' },
                    { label: 'Service Modifications', id: 'section-13' },
                    { label: 'Termination', id: 'section-14' },
                    { label: 'Representations & Warranties', id: 'section-15' },
                    { label: 'Disclaimers of Warranty', id: 'section-16' },
                    { label: 'Limitation of Liability', id: 'section-17' },
                    { label: 'Indemnification', id: 'section-18' },
                    { label: 'Governing Law & Disputes', id: 'section-19' },
                    { label: 'General Provisions', id: 'section-20' },
                    { label: 'Contact Information', id: 'section-21' },
                  ].map((item) => (
                    <li key={item.id}>
                      <a
                        href={`#${item.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }}
                        className="w-full text-left px-4 py-2.5 rounded-xl transition-all flex items-center gap-2 text-xs font-bold text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5 hover:text-gray-900 dark:hover:text-white cursor-pointer"
                      >
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>

              {/* Key commitments card */}
              <div className="bg-[#6366F1]/5 dark:bg-[#6366F1]/10 rounded-2xl p-6 border border-[#6366F1]/20">
                <h3 className="text-xs font-black text-[#6366F1] mb-3 uppercase tracking-[0.2em] font-mono">
                  Key Commitments
                </h3>
                <ul className="space-y-2.5">
                  {[
                    '7-day money-back guarantee',
                    'You own your content',
                    'No data sold to third parties',
                    '30 days notice on price changes',
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                      <span className="w-4 h-4 flex items-center justify-center rounded-full bg-emerald-500/10 border border-emerald-500/20 flex-shrink-0">
                        <i className="ri-check-line text-emerald-500 text-[10px]"></i>
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Contact card */}
              <div className="bg-gray-50 dark:bg-[#0F1629] rounded-2xl p-6 border border-gray-200 dark:border-gray-800">
                <h3 className="text-xs font-black text-gray-900 dark:text-white mb-4 uppercase tracking-[0.2em] font-mono border-b border-gray-100 dark:border-gray-800 pb-3">
                  Legal Contact
                </h3>
                <a
                  href="mailto:hello@jobos.dev"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#6366F1] text-white text-[10px] font-black uppercase tracking-widest rounded-full hover:bg-[#4F46E5] transition-all shadow-lg whitespace-nowrap"
                >
                  <i className="ri-mail-line"></i>
                  hello@jobos.dev
                </a>
              </div>
            </div>
          </aside>

          {/* ── MAIN CONTENT ── */}
          <div className="flex-1 max-w-4xl">

            {/* ── LAST UPDATED BANNER ── */}
            {!bannerDismissed && (
              <div
                className={`mb-6 rounded-2xl border transition-all duration-500 overflow-hidden ${
                  isNewUpdate
                    ? 'border-emerald-400/60 dark:border-emerald-500/50 bg-emerald-50 dark:bg-emerald-950/30 shadow-[0_0_24px_0_rgba(52,211,153,0.18)]'
                    : 'border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-[#141828]/50'
                }`}
                style={isNewUpdate ? { animation: 'termsBannerPulse 2.5s ease-in-out 2' } : {}}
              >
                <style>{`
                  @keyframes termsBannerPulse {
                    0%, 100% { box-shadow: 0 0 24px 0 rgba(52,211,153,0.18); }
                    50% { box-shadow: 0 0 40px 6px rgba(52,211,153,0.38); }
                  }
                `}</style>
                <div className="flex items-center gap-4 px-5 py-4">
                  <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 ${
                    isNewUpdate
                      ? 'bg-emerald-500/15 border border-emerald-400/30'
                      : 'bg-gray-200 dark:bg-white/10 border border-gray-300 dark:border-white/10'
                  }`}>
                    {isNewUpdate ? (
                      <i className="ri-refresh-line text-emerald-500 text-base"></i>
                    ) : (
                      <i className="ri-calendar-check-line text-gray-500 dark:text-gray-400 text-base"></i>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {isNewUpdate && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[10px] font-black uppercase tracking-widest whitespace-nowrap">
                          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse inline-block"></span>
                          Updated
                        </span>
                      )}
                      <span className={`text-sm font-bold ${
                        isNewUpdate
                          ? 'text-emerald-800 dark:text-emerald-300'
                          : 'text-gray-700 dark:text-gray-300'
                      }`}>
                        Last Updated: {LAST_UPDATED_DATE}
                      </span>
                      {isNewUpdate && (
                        <span className="text-xs text-emerald-700 dark:text-emerald-400 font-medium">
                          — This document has been revised since your last visit.
                        </span>
                      )}
                    </div>
                    {!isNewUpdate && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        You're up to date — no changes since your last visit.
                      </p>
                    )}
                  </div>

                  <button
                    onClick={handleDismiss}
                    className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full hover:bg-black/5 dark:hover:bg-white/10 transition-colors cursor-pointer"
                    title="Dismiss"
                  >
                    <i className={`ri-close-line text-sm ${
                      isNewUpdate ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400'
                    }`}></i>
                  </button>
                </div>
              </div>
            )}

            {/* Intro */}
            <div>
              <div className="flex flex-wrap gap-3 text-xs text-gray-500 dark:text-gray-400 mb-6">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 rounded-full font-semibold">
                  <i className="ri-calendar-line text-[#6366F1]"></i>
                  Last Updated: March 5, 2026
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 rounded-full font-semibold">
                  <i className="ri-time-line text-[#6366F1]"></i>
                  Effective: March 5, 2026
                </span>
              </div>

              <h1 className="text-3xl font-black text-gray-900 dark:text-white mb-6 uppercase tracking-tight">
                Terms of Service
              </h1>

              <div className="bg-[#FEF2F2] dark:bg-[#7F1D1D]/20 border border-[#EF4444]/30 dark:border-[#EF4444]/20 rounded-2xl p-6 mb-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#EF4444]/10 border border-[#EF4444]/20 flex-shrink-0">
                    <i className="ri-alert-line text-[#EF4444] text-lg"></i>
                  </div>
                  <div>
                    <p className="text-gray-900 dark:text-white font-bold mb-3 uppercase text-sm tracking-wide">
                      Please Read Carefully Before Using the Service
                    </p>
                    <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                      By creating an account, installing the Chrome Extension, or otherwise accessing or using any part of the Service, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service and all policies incorporated herein by reference, including the Privacy Policy available at{' '}
                      <a href="/privacy" className="text-[#6366F1] hover:underline font-medium">
                        jobos.dev/privacy
                      </a>.
                    </p>
                    <p className="text-sm text-gray-700 dark:text-gray-300 font-semibold">
                      If you do not agree to these Terms in their entirety, you are not authorized to use the Service in any manner.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 1: Definitions */}
            <CollapsibleSection id="section-1" num="01" title="Definitions">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                For the purposes of these Terms of Service, the following definitions shall apply:
              </p>
              <dl className="space-y-4">
                <DefinitionItem
                  term="Agreement"
                  definition="means these Terms of Service, together with the Privacy Policy and any additional policies, guidelines, or amendments published by JobOS from time to time, all of which are incorporated herein by reference."
                />
                <DefinitionItem
                  term="Chrome Extension"
                  definition="means the JobOS browser extension distributed via the Google Chrome Web Store, including all versions and updates thereof."
                />
                <DefinitionItem
                  term="Content"
                  definition="means any text, data, files, information, resume material, cover letters, application notes, contact records, company data, or other materials submitted, uploaded, stored, or generated through the Service by a User."
                />
                <DefinitionItem
                  term="Free Plan"
                  definition="means the no-cost subscription tier of the Service as described in Section 9.1 hereof."
                />
                <DefinitionItem
                  term="Intellectual Property Rights"
                  definition="means all patents, copyrights, trademarks, trade secrets, database rights, moral rights, and all other intellectual or industrial property rights of any nature, whether registered or unregistered, anywhere in the world."
                />
                <DefinitionItem
                  term="JobOS, we, us, or our"
                  definition="refers to TechFraser Inc., a Canadian corporation operating the JobOS platform, with a principal place of business in Canada."
                />
                <DefinitionItem
                  term="Pro Plan"
                  definition="means the paid subscription tier of the Service as described in Section 9.2 hereof."
                />
                <DefinitionItem
                  term="Service"
                  definition="means collectively the JobOS web application accessible at app.jobos.dev, the marketing website at jobos.dev, the Chrome Extension, all AI-powered features including the Application Copilot, and any other products, tools, or features offered by JobOS, whether now existing or hereafter developed."
                />
                <DefinitionItem
                  term="User, you, or your"
                  definition="means any individual who accesses, registers for, installs, or otherwise uses the Service."
                />
              </dl>
            </CollapsibleSection>

            {/* Section 2: Operator Identity */}
            <CollapsibleSection id="section-2" num="02" title="Operator Identity and Contact Information">
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                The Service is owned and operated by:
              </p>

              {/* Premium Operator Card */}
              <div className="relative overflow-hidden rounded-2xl border border-gray-200 dark:border-white/10 mb-6">
                {/* Gradient background strip */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#6366F1]/8 via-transparent to-emerald-500/5 dark:from-[#6366F1]/15 dark:via-transparent dark:to-emerald-500/10 pointer-events-none" />
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#6366F1] via-indigo-400 to-emerald-400 rounded-t-2xl" />

                <div className="relative p-6">
                  {/* Header row */}
                  <div className="flex items-center gap-4 mb-6 pb-5 border-b border-gray-100 dark:border-white/8">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#6366F1]/10 border border-[#6366F1]/20 flex-shrink-0">
                      <i className="ri-building-2-line text-[#6366F1] text-xl"></i>
                    </div>
                    <div>
                      <p className="text-lg font-black text-gray-900 dark:text-white tracking-tight">TechFraser Inc.</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 font-medium mt-0.5">Legal Operator of JobOS</p>
                    </div>
                    <div className="ml-auto">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-[11px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-widest whitespace-nowrap">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block"></span>
                        Active
                      </span>
                    </div>
                  </div>

                  {/* Info grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="flex items-start gap-3 p-4 rounded-xl bg-white/60 dark:bg-white/[0.03] border border-gray-100 dark:border-white/8">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#6366F1]/10 flex-shrink-0">
                        <i className="ri-user-star-line text-[#6366F1] text-sm"></i>
                      </div>
                      <div>
                        <p className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-[0.15em] mb-1">Operator</p>
                        <p className="text-sm font-bold text-gray-900 dark:text-white">TechFraser Inc.</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 p-4 rounded-xl bg-white/60 dark:bg-white/[0.03] border border-gray-100 dark:border-white/8">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-500/10 flex-shrink-0">
                        <i className="ri-verified-badge-line text-emerald-500 text-sm"></i>
                      </div>
                      <div>
                        <p className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-[0.15em] mb-1">Status</p>
                        <p className="text-sm font-bold text-gray-900 dark:text-white">Canadian Corporation</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 p-4 rounded-xl bg-white/60 dark:bg-white/[0.03] border border-gray-100 dark:border-white/8">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-500/10 flex-shrink-0">
                        <i className="ri-map-pin-2-line text-amber-500 text-sm"></i>
                      </div>
                      <div>
                        <p className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-[0.15em] mb-1">Location</p>
                        <p className="text-sm font-bold text-gray-900 dark:text-white">Canada 🍁</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 overflow-x-auto rounded-2xl border border-gray-200 dark:border-gray-800">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-[#0F1629]">
                      <th className="px-5 py-3.5 text-left text-xs font-black uppercase tracking-widest text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-800">Purpose</th>
                      <th className="px-5 py-3.5 text-left text-xs font-black uppercase tracking-widest text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-800">Contact</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { purpose: 'Contractual Inquiries', email: 'hello@jobos.dev' },
                      { purpose: 'Privacy Matters', email: 'privacy@jobos.dev' },
                      { purpose: 'Support Requests', email: 'support@jobos.dev' },
                    ].map((row, i) => (
                      <tr key={row.purpose} className={i % 2 === 0 ? 'bg-white dark:bg-[#0A0E1A]' : 'bg-gray-50/50 dark:bg-[#0F1629]/50'}>
                        <td className="px-5 py-3.5 font-semibold text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-800">{row.purpose}</td>
                        <td className="px-5 py-3.5 text-gray-600 dark:text-gray-400 border-b border-gray-100 dark:border-gray-800">
                          <a href={`mailto:${row.email}`} className="text-[#6366F1] hover:underline">{row.email}</a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CollapsibleSection>

            {/* Section 3: Eligibility and Account Registration */}
            <CollapsibleSection id="section-3" num="03" title="Eligibility and Account Registration">
              <div className="space-y-6">
                <SubSection title="3.1 Eligibility Requirements">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Access to and use of the Service is conditioned upon your satisfaction of all of the following eligibility requirements. By accessing the Service, you represent and warrant that:
                  </p>
                  <ul className="space-y-3">
                    {[
                      'you are a natural person who is at least sixteen (16) years of age;',
                      'you possess the legal capacity and authority to enter into a binding contract in the jurisdiction in which you reside;',
                      'your registration and use of the Service complies with all applicable laws, rules, and regulations in your jurisdiction;',
                      'you have not previously been suspended, terminated, or otherwise prohibited from accessing the Service; and',
                      'you are not accessing the Service on behalf of any automated system, bot, or non-human entity, except as expressly authorized in writing by JobOS.',
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-sm text-gray-700 dark:text-gray-300">
                        <span className="text-[#6366F1] font-mono text-xs mt-0.5 flex-shrink-0">({String.fromCharCode(97 + idx)})</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </SubSection>

                <SubSection title="3.2 Account Registration">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    To access certain features of the Service, you must register for an account by providing a valid email address and such other information as may be required during the registration process. You agree to:
                  </p>
                  <ul className="space-y-3">
                    {[
                      'provide accurate, current, and complete information during registration;',
                      'maintain and promptly update your account information to keep it accurate, current, and complete;',
                      'maintain the confidentiality of your account credentials and not disclose your password to any third party;',
                      'immediately notify JobOS at support@jobos.dev upon becoming aware of any actual or suspected unauthorized access to or use of your account; and',
                      'accept full responsibility for all activities that occur under your account, whether or not authorized by you.',
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-sm text-gray-700 dark:text-gray-300">
                        <span className="text-[#6366F1] font-mono text-xs mt-0.5 flex-shrink-0">({String.fromCharCode(97 + idx)})</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </SubSection>

                <SubSection title="3.3 Account Restrictions">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    You may maintain only one (1) account per individual. The creation of multiple accounts for the purpose of circumventing plan limitations, avoiding a suspension, or obtaining additional free trial access is expressly prohibited.
                  </p>
                  <div className="bg-[#FEF2F2] dark:bg-[#7F1D1D]/20 border border-[#EF4444]/30 dark:border-[#EF4444]/20 rounded-xl p-5">
                    <p className="text-sm text-gray-900 dark:text-white font-semibold">
                      JobOS reserves the right to terminate any or all accounts associated with a User found to be in violation of this restriction.
                    </p>
                  </div>
                </SubSection>
              </div>
            </CollapsibleSection>

            {/* Section 4: Description of Service */}
            <section id="description" className="mb-16 scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <span className="text-xs font-black text-[#6366F1] tracking-widest font-mono">04</span>
                <div className="h-px flex-1 bg-gray-100 dark:bg-white/5"></div>
              </div>
              <button
                onClick={() => setExpandedSections({ ...expandedSections, description: !expandedSections.description })}
                className="w-full flex items-center justify-between group mb-6 cursor-pointer"
              >
                <h2 className="text-3xl font-black text-gray-900 dark:text-white tracking-tight">
                  4. Description of the Service
                </h2>
                <i className={`ri-arrow-${expandedSections.description ? 'up' : 'down'}-s-line text-2xl text-gray-400 group-hover:text-[#6366F1] transition-colors`}></i>
              </button>

              {expandedSections.description && (
                <div className="space-y-6">
                  <p className="text-gray-700 dark:text-[#94A3B8] leading-relaxed">
                    JobOS provides a comprehensive job search management platform for individual job seekers, comprising the following modules and features:
                  </p>

                  <div className="space-y-4">
                    {[
                      { letter: 'a', title: 'Application Tracker', desc: 'a pipeline-based system enabling Users to track job applications across stages, including Bookmarked, Applying, Applied, Interviewing, Negotiating, and Accepted' },
                      { letter: 'b', title: 'AI Application Copilot', desc: 'an artificial intelligence feature that analyzes a job description, calculates a match score against the User\'s profile, generates tailored professional summaries, and produces cover letter drafts, powered by the Anthropic Claude API' },
                      { letter: 'c', title: 'AI Resume Builder', desc: 'a tool enabling Users to construct a Master Profile that serves as the basis for AI-tailored resume generation on a per-application basis' },
                      { letter: 'd', title: 'Chrome Extension', desc: 'a browser extension enabling one-click extraction of job data from job sites and one-click autofill of applicant tracking system ("ATS") platforms including Workday, Greenhouse, Lever, iCIMS, SmartRecruiters, Workable, Taleo, Rozee.pk, and Naukri.com' },
                      { letter: 'e', title: 'Job Discovery and Automated Agents', desc: 'tools enabling Users to configure search parameters and deploy automated agents that monitor job boards for matching opportunities' },
                      { letter: 'f', title: 'Interview Management', desc: 'scheduling and calendar management tools with integration support for Google Calendar and Microsoft Outlook' },
                      { letter: 'g', title: 'Network CRM', desc: 'a contact management system for tracking recruiters, hiring managers, and professional contacts' },
                      { letter: 'h', title: 'Watchlist', desc: 'a company tracking module enabling Users to monitor and manage a list of target employers' },
                      { letter: 'i', title: 'Playbook', desc: 'a structured, phase-based job search strategy and guidance framework' },
                      { letter: 'j', title: 'Growth Engine', desc: 'analytics dashboards reporting on application velocity and response rates, together with an AI-powered LinkedIn content generation tool' },
                    ].map((item) => (
                      <div key={item.letter} className="flex items-start gap-4 p-5 rounded-xl bg-gray-50 dark:bg-[#0D111A] border border-gray-200 dark:border-[#2D3148]">
                        <div className="w-8 h-8 flex-shrink-0 rounded-lg bg-[#6366F1]/10 border border-[#6366F1]/20 flex items-center justify-center">
                          <span className="text-[#6366F1] font-bold text-sm">({item.letter})</span>
                        </div>
                        <div>
                          <div className="font-bold text-gray-900 dark:text-white mb-1">{item.title}</div>
                          <div className="text-sm text-gray-600 dark:text-[#94A3B8] leading-relaxed">{item.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="bg-amber-50 dark:bg-[#7F1D1D]/20 border border-amber-200 dark:border-amber-800 rounded-xl p-6 border-2 border-amber-300 dark:border-amber-800">
                    <div className="flex items-start gap-3">
                      <i className="ri-information-line text-amber-600 dark:text-amber-400 text-xl flex-shrink-0 mt-0.5"></i>
                      <div>
                        <div className="font-bold text-amber-900 dark:text-amber-300 mb-2">Service Modifications</div>
                        <p className="text-sm text-amber-800 dark:text-amber-200/80 leading-relaxed">
                          JobOS reserves the right to modify, suspend, add to, or discontinue any feature or module of the Service at any time, subject to the notice provisions set forth in Section 13 hereof.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </section>

            {/* Section 5: Acceptable Use Policy */}
            <section id="acceptable" className="mb-16 scroll-mt-24">
              <div className="flex items-center gap-3 mb-6">
                <span className="text-xs font-black text-[#6366F1] tracking-widest font-mono">05</span>
                <div className="h-px flex-1 bg-gray-100 dark:bg-white/5"></div>
              </div>
              <button
                onClick={() => setExpandedSections({ ...expandedSections, acceptable: !expandedSections.acceptable })}
                className="w-full flex items-center justify-between group mb-6 cursor-pointer"
              >
                <h2 className="text-3xl font-black text-gray-900 dark:text-white tracking-tight">
                  5. Acceptable Use Policy
                </h2>
                <i className={`ri-arrow-${expandedSections.acceptable ? 'up' : 'down'}-s-line text-2xl text-gray-400 group-hover:text-[#6366F1] transition-colors`}></i>
              </button>

              {expandedSections.acceptable && (
                <div className="space-y-8">
                  {/* 5.1 Permitted Uses */}
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">5.1 Permitted Uses</h3>
                    <p className="text-gray-700 dark:text-[#94A3B8] leading-relaxed mb-4">
                      The Service is made available exclusively for the personal, non-commercial use of individual job seekers managing their own job search activities. Permitted uses include:
                    </p>
                    <div className="space-y-3">
                      {[
                        'tracking job applications that you are personally and genuinely pursuing',
                        'using AI-powered features to generate resume content and cover letters for roles to which you intend to apply',
                        'using the Chrome Extension to capture job postings you have personally identified and to autofill ATS forms for applications you are personally submitting',
                        'managing your own professional contacts and target company list',
                      ].map((item, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-4 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800">
                          <i className="ri-check-line text-emerald-600 dark:text-emerald-400 text-lg flex-shrink-0 mt-0.5"></i>
                          <span className="text-sm text-gray-700 dark:text-[#94A3B8] leading-relaxed">
                            <strong className="text-gray-900 dark:text-white">({String.fromCharCode(97 + idx)})</strong> {item}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* 5.2 Prohibited Uses */}
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">5.2 Prohibited Uses</h3>
                    <p className="text-gray-700 dark:text-[#94A3B8] leading-relaxed mb-4">
                      You agree that you shall not, directly or indirectly:
                    </p>
                    <div className="space-y-3">
                      {[
                        'use the Service to submit applications on behalf of any third party without their explicit, informed consent',
                        'use the Service to submit fraudulent, misleading, or materially false applications or to misrepresent your identity, qualifications, or experience to prospective employers',
                        'use the autofill or automation features of the Service to submit mass, indiscriminate, or unsolicited applications in a volume that constitutes abuse of employer application systems',
                        'copy, scrape, frame, mirror, republish, download, display, transmit, or distribute any portion of the Service or its content in any form or by any means',
                        'reverse-engineer, decompile, disassemble, or otherwise attempt to derive the source code of the Service or the Chrome Extension',
                        'sublicense, sell, resell, transfer, assign, or otherwise commercialize the Service or access thereto without express written consent from JobOS',
                        'use the Service to develop a competing product or service',
                        'access the Service by any means other than the interfaces expressly provided by JobOS, including through the use of scripts, bots, crawlers, or other automated tools',
                        'attempt to gain unauthorized access to any portion of the Service, its related systems, or the accounts of other Users',
                        'introduce any virus, trojan, worm, logic bomb, or other malicious code into the Service',
                        'take any action that imposes an unreasonable or disproportionate load on the infrastructure of the Service',
                      ].map((item, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-4 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800">
                          <i className="ri-close-line text-red-600 dark:text-red-400 text-lg flex-shrink-0 mt-0.5"></i>
                          <span className="text-sm text-gray-700 dark:text-[#94A3B8] leading-relaxed">
                            <strong className="text-gray-900 dark:text-white">({String.fromCharCode(97 + idx)})</strong> {item}
                          </span>
                        </div>
                      ))}
                    </div>

                    <div className="mt-6 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-xl p-6">
                      <div className="flex items-start gap-3">
                        <i className="ri-alarm-warning-line text-red-600 dark:text-red-400 text-2xl flex-shrink-0"></i>
                        <div>
                          <div className="font-bold text-red-900 dark:text-red-300 mb-2">Enforcement</div>
                          <p className="text-sm text-red-800 dark:text-red-200/80 leading-relaxed">
                            Any violation of this Section 5.2 may result in immediate suspension or termination of your account pursuant to Section 14, without refund of any amounts paid.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </section>

            {/* Section 6 */}
            <CollapsibleSection id="section-6" num="06" title="Chrome Extension — Specific Terms">
              <div className="space-y-8">
                <SubSection title="6.1 Scope of License">
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                    Subject to your compliance with this Agreement, JobOS grants you a limited, non-exclusive, non-transferable, revocable license to install and use the Chrome Extension solely for your personal job search activities in accordance with these Terms and Google's Chrome Web Store Developer Program Policies.
                  </p>
                  <div className="bg-[#6366F1]/5 dark:bg-[#6366F1]/10 border border-[#6366F1]/20 rounded-xl p-5">
                    <div className="flex items-start gap-3">
                      <i className="ri-chrome-line text-[#6366F1] text-2xl flex-shrink-0"></i>
                      <div>
                        <div className="font-bold text-gray-900 dark:text-white mb-2">Chrome Web Store Compliance</div>
                        <p className="text-sm text-gray-700 dark:text-[#94A3B8] leading-relaxed">
                          The Chrome Extension is distributed through the Google Chrome Web Store and is subject to Google's policies. Your use of the Extension must comply with both these Terms and Google's Chrome Web Store Developer Program Policies.
                        </p>
                      </div>
                    </div>
                  </div>
                </SubSection>

                <SubSection title="6.2 Extension Permissions">
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-5">
                    The Chrome Extension requests the following browser permissions, each of which is necessary for the operation of the Extension's core functionality:
                  </p>
                  <div className="overflow-x-auto rounded-2xl border border-gray-200 dark:border-white/10">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-[#141828]/80">
                          <th className="px-5 py-3.5 text-left text-xs font-black uppercase tracking-widest text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-white/10">
                            Permission
                          </th>
                          <th className="px-5 py-3.5 text-left text-xs font-black uppercase tracking-widest text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-white/10">
                            Operational Purpose
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { perm: 'activeTab', purpose: 'To read the content of the active job posting page in order to extract job data at the User\'s direction' },
                          { perm: 'storage', purpose: 'To store the User\'s autofill profile locally on the User\'s device for rapid ATS form completion' },
                          { perm: 'scripting', purpose: 'To inject the User\'s saved profile data into ATS application form fields' },
                          { perm: 'hostPermissions', purpose: 'To operate on supported job sites and ATS platforms, including LinkedIn, Indeed, Glassdoor, Workday, Greenhouse, Lever, Rozee.pk, Naukri.com, and others' },
                        ].map((row, i) => (
                          <tr key={i} className={i % 2 === 0 ? 'bg-white dark:bg-transparent' : 'bg-gray-50/50 dark:bg-white/[0.02]'}>
                            <td className="px-5 py-3.5 border-b border-gray-100 dark:border-white/5 last:border-b-0 align-top">
                              <code className="text-xs font-mono bg-gray-100 dark:bg-white/5 px-2 py-1 rounded text-gray-900 dark:text-white font-semibold">
                                {row.perm}
                              </code>
                            </td>
                            <td className="px-5 py-3.5 text-gray-600 dark:text-gray-400 border-b border-gray-100 dark:border-white/5 last:border-b-0 align-top">
                              {row.purpose}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </SubSection>

                <SubSection title="6.3 Extension Limitations">
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                    The Chrome Extension does not and shall not:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[
                      'operate in the background without a User-initiated action',
                      'access, read, or transmit passwords, financial data, or browsing history',
                      'collect data from web pages unrelated to job searching',
                      'transmit data to any server other than the JobOS backend and the AI processing services described in Section 7',
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-start gap-3 bg-gray-50 dark:bg-[#141828]/30 border border-gray-200 dark:border-white/10 rounded-xl p-4">
                        <span className="w-5 h-5 flex items-center justify-center rounded-full bg-red-500/10 border border-red-500/20 flex-shrink-0 mt-0.5">
                          <i className="ri-close-line text-red-500 text-xs"></i>
                        </span>
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          <span className="font-semibold text-gray-900 dark:text-white">({String.fromCharCode(97 + idx)})</span> {item}
                        </span>
                      </div>
                    ))}
                  </div>
                </SubSection>

                <SubSection title="6.4 Updates and Modifications">
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    JobOS may release updates to the Chrome Extension via the Chrome Web Store at any time. Such updates may modify, add, or remove features. Continued use of the Extension following an update constitutes acceptance of any modifications to the Extension's functionality.
                  </p>
                </SubSection>
              </div>
            </CollapsibleSection>

            {/* Section 7 */}
            <CollapsibleSection id="section-7" num="07" title="Artificial Intelligence Features — Disclaimer and Limitations">
              <div className="space-y-8">
                <SubSection title="7.1 AI Service Provider">
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    Certain features of the Service, including the Application Copilot, resume tailoring, cover letter generation, match scoring, and LinkedIn content generation, are powered by the Anthropic Claude API ("AI Service"). By using these features, you acknowledge and agree to the terms set forth in this Section 7 in their entirety.
                  </p>
                </SubSection>

                <SubSection title="7.2 Transmission of User Data to AI Service">
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                    When you use any AI-powered feature of the Service, the following categories of data are transmitted to the Anthropic Claude API for the sole purpose of generating the requested output:
                  </p>
                  <div className="space-y-2 mb-5">
                    {[
                      'the full text of the relevant job description',
                      'your resume content and Master Profile data, including professional summary, work history, education, and skills',
                      'any additional context you provide within the feature interface',
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <span className="text-[#6366F1] font-semibold text-sm flex-shrink-0">({String.fromCharCode(97 + idx)})</span>
                        <span className="text-sm text-gray-600 dark:text-gray-400">{item}</span>
                      </div>
                    ))}
                  </div>
                  <div className="bg-[#6366F1]/5 dark:bg-[#6366F1]/10 border border-[#6366F1]/20 rounded-xl p-5">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Such data is processed pursuant to JobOS's data processing agreement with Anthropic and is not used to train Anthropic's models. For further information regarding data handling, please refer to the Privacy Policy at{' '}
                      <a href="/privacy" className="text-[#6366F1] dark:text-[#818CF8] hover:underline font-medium">
                        jobos.dev/privacy
                      </a>.
                    </p>
                  </div>
                </SubSection>

                <SubSection title="7.3 Nature of AI-Generated Content">
                  <div className="bg-amber-500/5 dark:bg-amber-500/10 border border-amber-500/20 rounded-xl p-6">
                    <p className="text-sm font-black uppercase tracking-wider text-amber-600 dark:text-amber-400 mb-4">
                      YOU EXPRESSLY ACKNOWLEDGE AND AGREE THAT:
                    </p>
                  </div>

                  <div className="space-y-6">
                    {/* (a) Probabilistic nature */}
                    <div className="bg-gray-50 dark:bg-[#141828]/30 border border-gray-200 dark:border-white/10 rounded-xl p-5">
                      <p className="text-sm font-bold text-gray-900 dark:text-white mb-3">
                        (a) AI output is inherently probabilistic and imperfect.
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                        The Application Copilot and all other AI-powered features generate content based on statistical patterns and the specific inputs provided. Such outputs may contain factual inaccuracies, misrepresentations of your experience, inappropriate language, or content that does not reflect your professional standing.
                      </p>
                    </div>

                    {/* (b) Review required */}
                    <div className="bg-red-500/5 dark:bg-red-500/10 border border-red-500/20 rounded-xl p-5">
                      <p className="text-sm font-bold text-gray-900 dark:text-white mb-3">
                        (b) All AI-generated content must be reviewed prior to use.
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                        You are solely and exclusively responsible for reviewing, editing, approving, and accepting full responsibility for all AI-generated content — including resume summaries, cover letters, and LinkedIn posts — before submitting such content to any prospective employer, professional platform, or third party. JobOS expressly disclaims any responsibility for the consequences of submitting AI-generated content that has not been reviewed by you.
                      </p>
                    </div>

                    {/* (c) Not professional advice */}
                    <div className="bg-gray-50 dark:bg-[#141828]/30 border border-gray-200 dark:border-white/10 rounded-xl p-5">
                      <p className="text-sm font-bold text-gray-900 dark:text-white mb-3">
                        (c) AI-generated content does not constitute professional advice.
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                        Nothing generated by the AI features of the Service constitutes career advice, legal advice, or any other form of professional advisory service. All outputs are tools to assist your independent decision-making and should be treated as drafts requiring your judgment.
                      </p>
                    </div>

                    {/* (d) No guarantee of accuracy */}
                    <div className="bg-gray-50 dark:bg-[#141828]/30 border border-gray-200 dark:border-white/10 rounded-xl p-5">
                      <p className="text-sm font-bold text-gray-900 dark:text-white mb-3">
                        (d) No guarantee of accuracy or fitness for purpose.
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                        JobOS makes no representation or warranty, express or implied, that AI-generated content will be accurate, complete, grammatically correct, culturally appropriate, or fit for any particular purpose, including submission to any specific employer or for any specific role.
                      </p>
                    </div>

                    {/* (e) No employment guarantee */}
                    <div className="bg-red-500/5 dark:bg-red-500/10 border border-red-500/20 rounded-xl p-5">
                      <p className="text-sm font-bold text-gray-900 dark:text-white mb-3">
                        (e) No guarantee of employment outcomes.
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-3">
                        <span className="font-black uppercase">JOBOS EXPRESSLY DISCLAIMS ANY REPRESENTATION THAT USE OF AI-POWERED FEATURES WILL RESULT IN INCREASED INTERVIEW RATES, JOB OFFERS, OR EMPLOYMENT.</span>
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                        Hiring decisions are made by third-party employers based on numerous factors entirely outside the control of JobOS, including but not limited to the qualifications of competing applicants, internal employer policies, economic conditions, and recruiter discretion.
                      </p>
                    </div>

                    {/* (f) Misrepresentation liability */}
                    <div className="bg-red-500/5 dark:bg-red-500/10 border border-red-500/20 rounded-xl p-5">
                      <p className="text-sm font-bold text-gray-900 dark:text-white mb-3">
                        (f) Misrepresentation liability.
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                        You acknowledge that submitting AI-generated content that misrepresents your qualifications, experience, or identity to a prospective employer may constitute fraudulent misrepresentation under applicable law. JobOS accepts no liability whatsoever for any legal, professional, or reputational consequences arising from your submission of AI-generated content to third parties.
                      </p>
                    </div>
                  </div>
                </SubSection>
              </div>
            </CollapsibleSection>

            {/* Section 8 */}
            <CollapsibleSection id="section-8" num="08" title="Affiliate Programme Terms">
              <div className="space-y-8">
                <SubSection title="8.1 Affiliate Programme Overview">
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                    JobOS offers an Affiliate Programme designed to reward approved participants for bringing qualified subscribers to the Service. The Affiliate Programme is governed by the terms of this Section 8 and the accompanying Affiliate Programme Agreement.
                  </p>
                  <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-blue-600 dark:bg-blue-500 rounded-lg flex-shrink-0">
                        <i className="ri-links-line text-white text-lg"></i>
                      </div>
                      <div>
                        <p className="text-gray-900 dark:text-white font-bold mb-3">Affiliate Link Requirements</p>
                        <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                          All promotional materials must include a clear and prominent disclosure that the materials contain JobOS affiliate links. JobOS affiliate links are clickable links that redirect users to JobOS pages or services and are designed to facilitate the referral of qualified subscribers.
                        </p>
                      </div>
                    </div>
                  </div>
                </SubSection>

                <SubSection title="8.2 Affiliate Link Usage">
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                    Affiliate links may only be used in accordance with the terms of this Section 8. You must not modify, obscure, or hide the affiliate link in any way that would make it difficult for users to access the Service through the link.
                  </p>
                  <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-xl p-5">
                    <div className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-information-line text-amber-600 dark:text-amber-400"></i>
                      </div>
                      <p className="text-sm text-amber-900 dark:text-amber-200 leading-relaxed">
                        <strong className="font-bold">Payment Condition:</strong> Commissions are earned only upon receipt of cleared payment from the referred subscriber. Commissions on subscriptions that are subsequently refunded, charged back, or cancelled within the refund window shall be reversed accordingly.
                      </p>
                    </div>
                  </div>
                </SubSection>

                <SubSection title="8.3 Affiliate Link Disclosure">
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                    All promotional materials must include a clear and prominent disclosure that the materials contain JobOS affiliate links. JobOS affiliate links are clickable links that redirect users to JobOS pages or services and are designed to facilitate the referral of qualified subscribers.
                  </p>
                  <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center bg-blue-600 dark:bg-blue-500 rounded-lg flex-shrink-0">
                        <i className="ri-file-list-3-line text-white text-lg"></i>
                      </div>
                      <div>
                        <p className="text-gray-900 dark:text-white font-bold mb-3">Legal Compliance Required</p>
                        <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                          Participants must comply with all applicable laws and regulations governing the disclosure of material connections between the participant and JobOS, including the U.S. Federal Trade Commission's Endorsement Guides, the UK ASA CAP Code, and equivalent regulations in other jurisdictions. All promotional content must clearly and conspicuously disclose the existence of a referral relationship with JobOS.
                        </p>
                      </div>
                    </div>
                  </div>
                </SubSection>

                <SubSection title="8.4 Commission Structure">
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                    Commission rates, payment thresholds, and payout schedules shall be set forth in the Affiliate Programme Agreement provided to each approved participant. JobOS reserves the right to modify commission structures upon thirty (30) days' written notice to active participants. Commission modifications shall apply to Qualified Referrals occurring after the effective date of such modification.
                  </p>
                  <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-xl p-5">
                    <div className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-information-line text-amber-600 dark:text-amber-400"></i>
                      </div>
                      <p className="text-sm text-amber-900 dark:text-amber-200 leading-relaxed">
                        <strong className="font-bold">Payment Condition:</strong> Commissions are earned only upon receipt of cleared payment from the referred subscriber. Commissions on subscriptions that are subsequently refunded, charged back, or cancelled within the refund window shall be reversed accordingly.
                      </p>
                    </div>
                  </div>
                </SubSection>

                <SubSection title="8.5 Prohibited Promotional Practices">
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                    Participants in the Programme are strictly prohibited from:
                  </p>
                  <div className="space-y-3 mb-6">
                    {[
                      'making false, misleading, or unsubstantiated claims about the Service in promotional materials',
                      'using paid search advertising that targets the "JobOS" brand name or domain without prior written consent',
                      'sending unsolicited bulk email (spam) containing Referral Links',
                      'posting Referral Links on platforms in violation of those platforms\' terms of service',
                      'representing yourself as employees, agents, or official representatives of JobOS',
                      'engaging in any deceptive, manipulative, or unethical promotional practices'
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg">
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-close-circle-line text-red-600 dark:text-red-400"></i>
                        </div>
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          <span className="font-mono text-red-600 dark:text-red-400 mr-2">({String.fromCharCode(97 + idx)})</span>
                          {item}
                        </p>
                      </div>
                    ))}
                  </div>
                  <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-xl p-5">
                    <div className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-error-warning-line text-red-600 dark:text-red-400"></i>
                      </div>
                      <p className="text-sm text-red-900 dark:text-red-200 leading-relaxed">
                        <strong className="font-bold">Enforcement:</strong> Violation of this Section 8.5 shall result in immediate termination of Programme participation and forfeiture of all accrued but unpaid commissions.
                      </p>
                    </div>
                  </div>
                </SubSection>

                <SubSection title="8.6 Disclosure Requirements">
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                    Participants must comply with all applicable laws and regulations governing the disclosure of material connections between the participant and JobOS, including the U.S. Federal Trade Commission's Endorsement Guides, the UK ASA CAP Code, and equivalent regulations in other jurisdictions. All promotional content must clearly and conspicuously disclose the existence of a referral relationship with JobOS.
                  </p>
                  <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center bg-blue-600 dark:bg-blue-500 rounded-lg flex-shrink-0">
                        <i className="ri-file-list-3-line text-white text-lg"></i>
                      </div>
                      <div>
                        <p className="text-gray-900 dark:text-white font-bold mb-3">Legal Compliance Required</p>
                        <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                          Participants must comply with all applicable laws and regulations governing the disclosure of material connections between the participant and JobOS, including the U.S. Federal Trade Commission's Endorsement Guides, the UK ASA CAP Code, and equivalent regulations in other jurisdictions. All promotional content must clearly and conspicuously disclose the existence of a referral relationship with JobOS.
                        </p>
                      </div>
                    </div>
                  </div>
                </SubSection>

                <SubSection title="8.7 Programme Termination">
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                    JobOS reserves the right to modify, suspend, add to, or discontinue any feature or module of the Service at any time, subject to the notice provisions set forth in Section 13 hereof.
                  </p>
                  <div className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-xl p-5">
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                      <strong className="font-bold text-gray-900 dark:text-white">Liability Limitation:</strong> JobOS's liability upon Programme termination is limited solely to the payment of such validly accrued commissions.
                    </p>
                  </div>
                </SubSection>
              </div>
            </CollapsibleSection>

            {/* Section 9: Subscription Plans and Payment Terms */}
            <section className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <span className="text-sm font-black text-[#6366F1] tracking-widest font-mono">09</span>
                <div className="h-px flex-1 bg-gray-100 dark:bg-white/5"></div>
              </div>
              <button
                onClick={() => setExpandedSections({ ...expandedSections, subscription: !expandedSections.subscription })}
                className="w-full flex items-center justify-between group cursor-pointer mb-6"
              >
                <div className="flex items-center gap-4">
                  <span className="text-sm font-black text-[#6366F1] tracking-widest font-mono">09</span>
                  <div className="h-px flex-1 bg-gray-200 dark:bg-white/10 w-8"></div>
                  <h2 className="text-2xl lg:text-3xl font-extrabold text-gray-900 dark:text-white">
                    Subscription Plans and Payment Terms
                  </h2>
                </div>
                <i className={`ri-arrow-down-s-line text-2xl text-gray-400 transition-transform ${expandedSections.subscription ? 'rotate-180' : ''}`}></i>
              </button>

              {expandedSections.subscription && (
                <div className="pl-0 lg:pl-16">
                  {/* Section 9.1: Free Plan */}
                  <div className="mb-8">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">9.1 Free Plan</h3>
                    <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                      JobOS offers a free tier of the Service requiring no payment and no credit card ("Free Plan"). The features, limitations, and usage quotas applicable to the Free Plan are as published on the pricing page at <a href="https://jobos.dev/#pricing" className="text-[#6366F1] hover:underline">jobos.dev/#pricing</a>.
                    </p>
                    <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-xl p-5">
                      <div className="flex items-start gap-3">
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-information-line text-blue-600 dark:text-blue-400"></i>
                        </div>
                        <p className="text-sm text-blue-900 dark:text-blue-200 leading-relaxed">
                          JobOS reserves the right to modify the features and limitations of the Free Plan at any time upon reasonable notice to existing Free Plan users.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Section 9.2: Pro Plan */}
                  <div className="mb-8">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">9.2 Pro Plan</h3>
                    <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                      JobOS offers a paid subscription tier providing access to enhanced features including, without limitation, unlimited AI resume tailoring, Application Copilot, ATS autofill, AI job agents, and advanced analytics ("Pro Plan").
                    </p>

                    {/* 9.2(a) Subscription Term */}
                    <div className="mb-6 p-5 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-xl">
                      <h4 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                        <span className="text-[#6366F1] font-mono text-sm">(a)</span>
                        Subscription Term
                      </h4>
                      <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                        The Pro Plan is available on a monthly or annual subscription basis, as selected by the User at the time of subscription. Each subscription term commences on the date of initial payment and renews automatically for successive equivalent periods unless cancelled in accordance with Section 9.2(c).
                      </p>
                    </div>

                    {/* 9.2(b) Payment Authorization */}
                    <div className="mb-6 p-5 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-xl">
                      <h4 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                        <span className="text-[#6366F1] font-mono text-sm">(b)</span>
                        Payment Authorization
                      </h4>
                      <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
                        By subscribing to the Pro Plan, you authorize JobOS and its designated payment processor to charge your designated payment method the applicable subscription fee on a recurring basis at the commencement of each subscription term.
                      </p>
                      <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                        You represent and warrant that:
                      </p>
                      <ul className="mt-3 space-y-2">
                        {[
                          'the payment information you provide is accurate and complete',
                          'you are authorized to use the designated payment method',
                          'you will promptly update your payment information in the event of changes'
                        ].map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                            <span className="font-mono text-[#6366F1] mt-0.5">({['i', 'ii', 'iii'][idx]})</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* 9.2(c) Cancellation */}
                    <div className="mb-6 p-5 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-xl">
                      <h4 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                        <span className="text-[#6366F1] font-mono text-sm">(c)</span>
                        Cancellation
                      </h4>
                      <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
                        You may cancel your Pro Plan subscription at any time by accessing <span className="font-mono bg-gray-200 dark:bg-gray-800 px-2 py-0.5 rounded">Settings → Billing</span> within the Service. Cancellation takes effect at the conclusion of the then-current subscription term. You will retain access to Pro Plan features through the end of the paid period.
                      </p>
                      <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mt-3">
                        <p className="text-sm text-amber-900 dark:text-amber-200">
                          <strong className="font-bold">No Prorated Refunds:</strong> JobOS does not provide prorated refunds for partial subscription periods, except as expressly provided in Section 9.4.
                        </p>
                      </div>
                    </div>

                    {/* 9.2(d) Failed Payments */}
                    <div className="mb-6 p-5 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-xl">
                      <h4 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                        <span className="text-red-600 dark:text-red-400 font-mono text-sm">(d)</span>
                        Failed Payments
                      </h4>
                      <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                        In the event that a scheduled payment cannot be processed, JobOS will make reasonable attempts to notify you via the email address associated with your account. If payment is not received within seven (7) days of the renewal date, JobOS reserves the right to downgrade your account to the Free Plan without further notice and without liability.
                      </p>
                    </div>
                  </div>

                  {/* Section 9.3: Price Modifications */}
                  <div className="mb-8">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">9.3 Price Modifications</h3>
                    <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                      JobOS reserves the right to modify subscription pricing at any time. In the event of a price increase applicable to existing subscribers, JobOS shall provide not less than thirty (30) days' prior written notice by email to the address associated with your account.
                    </p>
                    <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                      Price increases shall take effect at the commencement of the first renewal period following the notice period. Your continued use of the Pro Plan after the effective date of a price change constitutes acceptance of the new pricing.
                    </p>
                    <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-xl p-5">
                      <div className="flex items-start gap-3">
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-information-line text-blue-600 dark:text-blue-400"></i>
                        </div>
                        <p className="text-sm text-blue-900 dark:text-blue-200 leading-relaxed">
                          <strong className="font-bold">Your Right to Cancel:</strong> If you do not accept the new pricing, you must cancel your subscription prior to the next renewal date.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Section 9.4: Refund Policy */}
                  <div className="mb-8">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">9.4 Refund Policy</h3>

                    {/* 9.4(a) Seven-Day Guarantee */}
                    <div className="mb-6 p-5 bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800 rounded-xl">
                      <h4 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                        <span className="text-emerald-600 dark:text-emerald-400 font-mono text-sm">(a)</span>
                        Seven-Day Satisfaction Guarantee
                      </h4>
                      <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                        JobOS offers a seven (7) day money-back guarantee applicable to new Pro Plan subscriptions only. If you are not satisfied with the Pro Plan for any reason, you may request a full refund of your initial subscription payment by contacting <a href="mailto:support@jobos.dev" className="text-[#6366F1] hover:underline font-medium">support@jobos.dev</a> within seven (7) calendar days of your initial subscription date. Refund requests received after this seven (7) day period shall not be eligible for a refund under this guarantee.
                      </p>
                    </div>

                    {/* 9.4(b) Conditions */}
                    <div className="mb-6 p-5 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-xl">
                      <h4 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                        <span className="text-[#6366F1] font-mono text-sm">(b)</span>
                        Conditions for Refund Eligibility
                      </h4>
                      <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
                        To be eligible for a refund under Section 9.4(a), you must:
                      </p>
                      <ul className="space-y-2">
                        {[
                          'be a first-time Pro Plan subscriber',
                          'submit your refund request within the seven (7) day window specified above',
                          'provide the email address associated with your account and the date of your subscription'
                        ].map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                            <span className="font-mono text-[#6366F1] mt-0.5">({['i', 'ii', 'iii'][idx]})</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* 9.4(c) Renewals */}
                    <div className="mb-6 p-5 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-xl">
                      <h4 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                        <span className="text-amber-600 dark:text-amber-400 font-mono text-sm">(c)</span>
                        Refund for JobOS-Initiated Termination
                      </h4>
                      <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                        In the event that JobOS terminates your account for reasons other than a violation of this Agreement, you shall be entitled to a prorated refund of any prepaid subscription fees attributable to the unused portion of your then-current subscription term. This shall constitute JobOS's sole obligation to you in connection with such termination.
                      </p>
                    </div>

                    {/* 9.4(d) JobOS-Initiated Termination */}
                    <div className="mb-6 p-5 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-xl">
                      <h4 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                        <span className="text-[#6366F1] font-mono text-sm">(d)</span>
                        Refund for JobOS-Initiated Termination
                      </h4>
                      <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                        In the event that JobOS terminates your account for reasons other than a violation of this Agreement, you shall be entitled to a prorated refund of any prepaid subscription fees attributable to the unused portion of your then-current subscription term. This shall constitute JobOS's sole obligation to you in connection with such termination.
                      </p>
                    </div>

                    {/* 9.4(e) Exceptions */}
                    <div className="mb-6 p-5 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-xl">
                      <h4 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                        <span className="text-red-600 dark:text-red-400 font-mono text-sm">(e)</span>
                        Exceptions
                      </h4>
                      <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
                        Notwithstanding anything to the contrary herein, no refund shall be issued:
                      </p>
                      <ul className="space-y-2">
                        {[
                          'where account suspension or termination is attributable to a violation of this Agreement',
                          'where usage patterns indicate abuse of the refund policy, including but not limited to repeated subscription and refund cycles',
                          'in cases of fraud or misrepresentation'
                        ].map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                            <span className="font-mono text-red-600 dark:text-red-400 mt-0.5">({['i', 'ii', 'iii'][idx]})</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* 9.4(f) Processing */}
                    <div className="p-5 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-xl">
                      <h4 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                        <span className="text-[#6366F1] font-mono text-sm">(f)</span>
                        Processing
                      </h4>
                      <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                        Approved refunds will be processed to the original payment method within five (5) to ten (10) business days, subject to the policies of your payment provider. JobOS shall not be responsible for delays attributable to financial institutions.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </section>

            {/* Section 10 */}
            <CollapsibleSection id="section-10" num="10" title="Intellectual Property">
              <div className="space-y-8">
                <SubSection title="10.1 JobOS Property">
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                    The Service and all components thereof — including but not limited to the software, source code, algorithms, user interface designs, graphics, logos, trademarks, trade names, service marks, copy, databases, and all other content and materials — are the exclusive property of TechFraser Inc. and are protected by applicable intellectual property laws, including copyright, trademark, and trade secret laws of Canada and other jurisdictions.
                  </p>
                  <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-xl p-5">
                    <div className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-copyright-line text-amber-600 dark:text-amber-400"></i>
                      </div>
                      <p className="text-sm text-amber-900 dark:text-amber-200 leading-relaxed">
                        <strong className="font-bold">No Transfer of Rights:</strong> Nothing in this Agreement shall be construed as transferring any Intellectual Property Rights to you.
                      </p>
                    </div>
                  </div>
                </SubSection>

                <SubSection title="10.2 License Grant to User">
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                    Subject to your compliance with this Agreement and, where applicable, payment of applicable subscription fees, JobOS grants you a limited, non-exclusive, non-transferable, non-sublicensable, revocable license to access and use the Service solely for your personal, non-commercial job search activities.
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                    This license does not include the right to:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[
                      'sublicense or transfer access to the Service',
                      'reproduce, modify, or create derivative works of the Service',
                      'publicly display or perform any component of the Service',
                      'use the Service for any purpose not expressly authorized herein'
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-start gap-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                        <span className="w-5 h-5 flex items-center justify-center rounded-full bg-red-500/10 border border-red-500/20 flex-shrink-0 mt-0.5">
                          <i className="ri-close-line text-red-500 text-xs"></i>
                        </span>
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          <span className="font-mono text-red-600 dark:text-red-400 mr-2">({String.fromCharCode(97 + idx)})</span>
                          {item}
                        </span>
                      </div>
                    ))}
                  </div>
                </SubSection>

                <SubSection title="10.3 User Content License">
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                    You retain all ownership rights in and to the Content you submit to the Service. By submitting Content, you grant JobOS a worldwide, non-exclusive, royalty-free, fully paid-up license to use, store, process, reproduce, and display your Content solely to the extent necessary to operate and provide the Service to you.
                  </p>
                  <div className="bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-5">
                    <div className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-shield-check-line text-emerald-600 dark:text-emerald-400"></i>
                      </div>
                      <p className="text-sm text-emerald-900 dark:text-emerald-200 leading-relaxed">
                        <strong className="font-bold">You Own Your Content:</strong> This license terminates upon deletion of your Content or account, subject to the data retention provisions of the Privacy Policy.
                      </p>
                    </div>
                  </div>
                </SubSection>

                <SubSection title="10.4 Feedback">
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    If you provide JobOS with suggestions, ideas, enhancement requests, or other feedback regarding the Service ("Feedback"), you hereby assign to JobOS all right, title, and interest in and to such Feedback, including all Intellectual Property Rights therein. JobOS may use such Feedback without restriction and without any obligation of compensation, attribution, or confidentiality to you.
                  </p>
                </SubSection>
              </div>
            </CollapsibleSection>

            {/* Section 11 */}
            <CollapsibleSection id="section-11" num="11" title="Third-Party Services and Links">
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-5">
                The Service integrates with or makes reference to third-party services, including Supabase, Anthropic, Google Calendar, Microsoft Outlook, the Chrome Web Store, LinkedIn, and various job boards and ATS platforms.
              </p>
              <div className="bg-amber-500/5 dark:bg-amber-500/10 border border-amber-500/20 rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-500/10 border border-amber-500/20 flex-shrink-0">
                    <i className="ri-links-line text-amber-500 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-gray-900 dark:text-white font-bold mb-3">Third-Party Disclaimer</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                      JobOS does not control such third-party services and is not responsible for their content, functionality, availability, or terms of use. Your use of any third-party service is governed solely by that service's own terms and privacy policies. JobOS expressly disclaims all liability arising from your use of or reliance on any third-party service.
                    </p>
                  </div>
                </div>
              </div>
            </CollapsibleSection>

            {/* Section 12 */}
            <CollapsibleSection id="section-12" num="12" title="Privacy">
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-5">
                Your use of the Service is subject to the JobOS Privacy Policy, which is incorporated into this Agreement by reference and is available at{' '}
                <a href="/privacy" className="text-[#6366F1] dark:text-[#818CF8] hover:underline font-medium">
                  jobos.dev/privacy
                </a>.
              </p>
              <div className="bg-[#6366F1]/5 dark:bg-[#6366F1]/10 border border-[#6366F1]/20 rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#6366F1]/10 border border-[#6366F1]/20 flex-shrink-0">
                    <i className="ri-shield-check-line text-[#6366F1] text-lg"></i>
                  </div>
                  <div>
                    <p className="text-gray-900 dark:text-white font-bold mb-3">Your Consent</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                      By using the Service, you consent to the collection, use, storage, and disclosure of your information as described in the Privacy Policy.
                    </p>
                  </div>
                </div>
              </div>
            </CollapsibleSection>

            {/* Section 13 */}
            <CollapsibleSection id="section-13" num="13" title="Service Modifications, Suspension, and Discontinuation">
              <div className="space-y-8">
                <SubSection title="13.1 Modifications">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    JobOS reserves the right to modify, update, or change the Service — including adding, altering, or removing features, modules, or content — at any time.
                  </p>
                  <div className="bg-[#6366F1]/5 dark:bg-[#6366F1]/10 border border-[#6366F1]/20 rounded-xl p-5">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      <strong className="text-gray-900 dark:text-white">Notice Period:</strong> For modifications that materially reduce the core functionality of a paid subscription, JobOS will provide not less than fourteen (14) days' prior notice via email or in-app notification.
                    </p>
                  </div>
                </SubSection>

                <SubSection title="13.2 Scheduled Maintenance">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    JobOS may take the Service offline periodically for maintenance, upgrades, or security purposes. JobOS will endeavor to provide advance notice of scheduled maintenance where reasonably practicable.
                  </p>
                </SubSection>

                <SubSection title="13.3 Discontinuation">
                  <div className="bg-amber-500/5 dark:bg-amber-500/10 border border-amber-500/20 rounded-xl p-5">
                    <div className="flex items-start gap-3">
                      <i className="ri-information-line text-amber-500 text-lg mt-0.5 flex-shrink-0"></i>
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                        <strong className="text-gray-900 dark:text-white">Consumer Protection Rights:</strong> Nothing in this Section 13 shall limit the rights of Users located in the European Union or the United Kingdom to bring claims before their local courts or consumer protection authorities pursuant to applicable mandatory consumer protection laws.
                      </p>
                    </div>
                  </div>
                </SubSection>
              </div>
            </CollapsibleSection>

            {/* Section 14 */}
            <CollapsibleSection id="section-14" num="14" title="Termination">
              <div className="space-y-8">
                <SubSection title="14.1 Termination by User">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    You may terminate your account at any time by accessing <strong className="text-gray-900 dark:text-white">Settings → Data → Delete Account</strong> within the Service.
                  </p>
                  <div className="bg-gray-50 dark:bg-[#141828]/50 border border-gray-200 dark:border-white/10 rounded-xl p-5">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Upon account deletion, your right to use the Service terminates immediately, and your personal data will be deleted in accordance with the retention schedules set forth in the Privacy Policy.
                    </p>
                  </div>
                </SubSection>

                <SubSection title="14.2 Termination by JobOS">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
                    JobOS reserves the right to suspend or permanently terminate your access to the Service, with or without prior notice, in the event that:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                    {[
                      { letter: 'a', text: 'you breach any provision of this Agreement' },
                      { letter: 'b', text: 'you engage in fraudulent, abusive, or illegal activity in connection with the Service' },
                      { letter: 'c', text: 'your continued use poses a security risk to the Service or other Users' },
                      { letter: 'd', text: 'JobOS is required to do so by applicable law or the order of a competent authority' },
                    ].map((item) => (
                      <div key={item.letter} className="bg-red-500/5 dark:bg-red-500/10 border border-red-500/20 rounded-xl p-4 flex items-start gap-3">
                        <span className="w-6 h-6 flex items-center justify-center rounded-full bg-red-500/10 border border-red-500/20 flex-shrink-0 text-xs font-black text-red-500">
                          {item.letter}
                        </span>
                        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{item.text}</p>
                      </div>
                    ))}
                  </div>
                  <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-xl p-5">
                    <div className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-error-warning-line text-red-600 dark:text-red-400"></i>
                      </div>
                      <p className="text-sm text-red-900 dark:text-red-200 leading-relaxed">
                        <strong className="font-bold">Enforcement:</strong> Violation of this Section 14.2 shall result in immediate termination of your account and suspension of your access to the Service.
                      </p>
                    </div>
                  </div>
                </SubSection>

                <SubSection title="14.3 Effect of Termination">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
                    Upon termination of your account for any reason:
                  </p>
                  <BulletList items={[
                    { label: '(a)', desc: 'all licenses granted to you under this Agreement terminate immediately' },
                    { label: '(b)', desc: 'you must cease all use of the Service and uninstall the Chrome Extension' },
                    { label: '(c)', desc: 'any accrued payment obligations remain due and payable' },
                    { label: '(d)', desc: 'the following Sections shall survive termination: Sections 1, 7.3, 10, 15, 16, 17, 18, and 20' },
                  ]} />
                </SubSection>
              </div>
            </CollapsibleSection>

            {/* Section 15 */}
            <CollapsibleSection id="section-15" num="15" title="Representations and Warranties">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
                You represent and warrant to JobOS that:
              </p>
              <div className="grid grid-cols-1 gap-4">
                {[
                  { letter: 'a', text: 'you have the full legal capacity and authority to enter into and be bound by this Agreement' },
                  { letter: 'b', text: 'your use of the Service will comply with all applicable laws, rules, and regulations' },
                  { letter: 'c', text: 'all information you provide in connection with your account and use of the Service is truthful, accurate, and complete' },
                  { letter: 'd', text: 'you will not use the Service in any manner that infringes the Intellectual Property Rights or other rights of any third party' },
                ].map((item) => (
                  <div key={item.letter} className="bg-emerald-500/5 dark:bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-5 flex items-start gap-3">
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-emerald-500/10 border border-emerald-500/20 flex-shrink-0 text-xs font-black text-emerald-500">
                      {item.letter}
                    </span>
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{item.text}</p>
                  </div>
                ))}
              </div>
            </CollapsibleSection>

            {/* Section 16 */}
            <CollapsibleSection id="section-16" num="16" title="Disclaimers of Warranty">
              <div className="bg-red-500/5 dark:bg-red-500/10 border-2 border-red-500/30 dark:border-red-500/20 rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-red-500/10 border border-red-500/20 flex-shrink-0">
                    <i className="ri-error-warning-line text-red-500 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-gray-900 dark:text-white font-black mb-4 uppercase tracking-wider text-sm">
                      EXCLUSION OF CONSEQUENTIAL DAMAGES
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                      TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL JOBOS OR TECHFRASER INC. BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES OF ANY KIND — INCLUDING LOSS OF PROFITS, LOSS OF REVENUE, LOSS OF DATA, LOSS OF GOODWILL, COST OF SUBSTITUTE SERVICES, OR LOSS OF EMPLOYMENT OPPORTUNITIES — ARISING OUT OF OR IN CONNECTION WITH THIS AGREEMENT OR YOUR USE OF OR INABILITY TO USE THE SERVICE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
                    </p>
                  </div>
                </div>
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
                To the fullest extent permitted by applicable law, JobOS expressly disclaims all warranties, including but not limited to:
              </p>

              <div className="space-y-4 mb-6">
                {[
                  { letter: 'a', text: 'IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT' },
                  { letter: 'b', text: 'WARRANTIES ARISING FROM COURSE OF DEALING, USAGE, OR TRADE PRACTICE' },
                  { letter: 'c', text: 'WARRANTIES THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, SECURE, OR FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS' },
                  { letter: 'd', text: 'WARRANTIES REGARDING THE ACCURACY, COMPLETENESS, RELIABILITY, OR SUITABILITY OF ANY AI-GENERATED CONTENT, MATCH SCORES, OR RESUME TAILORING OUTPUT' },
                  { letter: 'e', text: 'WARRANTIES THAT USE OF THE SERVICE WILL RESULT IN ANY PARTICULAR EMPLOYMENT OUTCOME, INCLUDING INCREASED INTERVIEW RATES, JOB OFFERS, OR EMPLOYMENT' },
                ].map((item) => (
                  <div key={item.letter} className="bg-gray-50 dark:bg-[#141828]/50 border border-gray-200 dark:border-white/10 rounded-xl p-5 flex items-start gap-3">
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-gray-200 dark:bg-white/10 flex-shrink-0 text-xs font-black text-gray-600 dark:text-gray-400">
                      {item.letter}
                    </span>
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed font-medium">{item.text}</p>
                  </div>
                ))}
              </div>

              <div className="bg-amber-500/5 dark:bg-amber-500/10 border border-amber-500/20 rounded-xl p-5">
                <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                  <strong className="text-gray-700 dark:text-gray-300">Jurisdictional Note:</strong> Some jurisdictions do not permit the exclusion or limitation of incidental or consequential damages. In such jurisdictions, JobOS's liability shall be limited to the maximum extent permitted by applicable law.
                </p>
              </div>
            </CollapsibleSection>

            {/* Section 17: Limitation of Liability */}
            <CollapsibleSection id="section-17" num="17" title="Limitation of Liability">
              <div className="space-y-6">
                <div className="bg-red-500/5 dark:bg-red-500/10 border-2 border-red-500/30 dark:border-red-500/20 rounded-2xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-red-500/10 border border-red-500/20 flex-shrink-0">
                      <i className="ri-error-warning-line text-red-500 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-gray-900 dark:text-white font-black mb-4 uppercase tracking-wider text-sm">
                        EXCLUSION OF CONSEQUENTIAL DAMAGES
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                        TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL JOBOS OR TECHFRASER INC. BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES OF ANY KIND — INCLUDING LOSS OF PROFITS, LOSS OF REVENUE, LOSS OF DATA, LOSS OF GOODWILL, COST OF SUBSTITUTE SERVICES, OR LOSS OF EMPLOYMENT OPPORTUNITIES — ARISING OUT OF OR IN CONNECTION WITH THIS AGREEMENT OR YOUR USE OF OR INABILITY TO USE THE SERVICE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-amber-500/5 dark:bg-amber-500/10 border border-amber-500/20 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-500/10 border border-amber-500/20 flex-shrink-0">
                      <i className="ri-money-dollar-circle-line text-amber-500 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-gray-900 dark:text-white font-bold mb-3">Aggregate Liability Cap</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                        IN NO EVENT SHALL JOBOS'S AGGREGATE LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR IN CONNECTION WITH THIS AGREEMENT EXCEED THE GREATER OF: (A) THE TOTAL AMOUNT PAID BY YOU TO JOBOS IN THE TWELVE (12) MONTHS IMMEDIATELY PRECEDING THE EVENT GIVING RISE TO THE CLAIM; OR (B) ONE HUNDRED UNITED STATES DOLLARS (USD $100).
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-[#141828]/50 border border-gray-200 dark:border-white/10 rounded-xl p-5">
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-3">
                    THE FOREGOING LIMITATIONS APPLY REGARDLESS OF THE THEORY OF LIABILITY, WHETHER BASED IN CONTRACT, TORT (INCLUDING NEGLIGENCE), STRICT LIABILITY, STATUTE, OR ANY OTHER LEGAL OR EQUITABLE THEORY.
                  </p>
                  <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mt-3">
                    <p className="text-xs text-amber-900 dark:text-amber-200 leading-relaxed">
                      <strong className="font-bold">Jurisdictional Note:</strong> SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OR LIMITATION OF INCIDENTAL OR CONSEQUENTIAL DAMAGES. IN SUCH JURISDICTIONS, JOBOS'S LIABILITY SHALL BE LIMITED TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW.
                    </p>
                  </div>
                </div>
              </div>
            </CollapsibleSection>

            {/* Section 18: Indemnification */}
            <CollapsibleSection id="section-18" num="18" title="Indemnification">
              <div className="space-y-6">
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  You agree to defend, indemnify, and hold harmless JobOS, TechFraser Inc., and their respective heirs, successors, and assigns from and against any and all claims, actions, proceedings, losses, liabilities, damages, judgments, penalties, fines, costs, and expenses (including reasonable attorneys' fees) arising out of or relating to:
                </p>

                <div className="grid grid-cols-1 gap-4">
                  {[
                    { letter: 'a', text: 'your access to or use of the Service' },
                    { letter: 'b', text: 'your breach of any representation, warranty, obligation, or covenant set forth in this Agreement' },
                    { letter: 'c', text: 'your violation of any applicable law, rule, or regulation' },
                    { letter: 'd', text: 'your infringement or misappropriation of any third-party Intellectual Property Rights' },
                    { letter: 'e', text: 'any Content you submit, transmit, or make available through the Service' },
                    { letter: 'f', text: 'your submission to a third-party employer of AI-generated content that is inaccurate, misleading, or constitutes a misrepresentation' },
                  ].map((item) => (
                    <div key={item.letter} className="bg-red-500/5 dark:bg-red-500/10 border border-red-500/20 rounded-xl p-5 flex items-start gap-3">
                      <span className="w-6 h-6 flex items-center justify-center rounded-full bg-red-500/10 border border-red-500/20 flex-shrink-0 text-xs font-black text-red-500">
                        {item.letter}
                      </span>
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{item.text}</p>
                    </div>
                  ))}
                </div>

                <div className="bg-[#6366F1]/5 dark:bg-[#6366F1]/10 border border-[#6366F1]/20 rounded-xl p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-shield-line text-[#6366F1]"></i>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                      <strong className="text-gray-900 dark:text-white">Defense Rights:</strong> JobOS reserves the right, at its own expense, to assume exclusive control of the defense of any matter otherwise subject to indemnification by you, in which case you agree to cooperate fully with JobOS in such defense.
                    </p>
                  </div>
                </div>
              </div>
            </CollapsibleSection>

            {/* Section 19: Governing Law, Jurisdiction, and Dispute Resolution */}
            <CollapsibleSection id="section-19" num="19" title="Governing Law, Jurisdiction, and Dispute Resolution">
              <div className="space-y-8">
                <SubSection title="19.1 Governing Law">
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    This Agreement shall be governed by and construed in accordance with the laws of the Province of Ontario, Canada, without regard to its conflict of laws principles.
                  </p>
                </SubSection>

                <SubSection title="19.2 Informal Resolution">
                  <div className="bg-[#6366F1]/5 dark:bg-[#6366F1]/10 border border-[#6366F1]/20 rounded-xl p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#6366F1]/10 border border-[#6366F1]/20 flex-shrink-0">
                        <i className="ri-chat-3-line text-[#6366F1] text-lg"></i>
                      </div>
                      <div>
                        <p className="text-gray-900 dark:text-white font-bold mb-3">Good Faith Resolution Required</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                          In the event of any dispute, claim, or controversy arising out of or relating to this Agreement or the Service, the parties agree to attempt in good faith to resolve such dispute informally by contacting JobOS at{' '}
                          <a href="mailto:hello@jobos.dev" className="text-[#6366F1] hover:underline font-medium">
                            hello@jobos.dev
                          </a>{' '}
                          prior to initiating formal legal proceedings. The parties shall allow a period of thirty (30) days for good faith informal resolution before either party may initiate formal proceedings.
                        </p>
                      </div>
                    </div>
                  </div>
                </SubSection>

                <SubSection title="19.3 Jurisdiction">
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    Subject to Section 19.2, each party irrevocably submits to the exclusive jurisdiction of the courts of Ontario, Canada for the resolution of any dispute arising out of or in connection with this Agreement.
                  </p>
                </SubSection>

                <SubSection title="19.4 EU and UK Users">
                  <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-xl p-5">
                    <div className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-global-line text-blue-600 dark:text-blue-400"></i>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                        <strong className="text-gray-900 dark:text-white">Consumer Protection Rights:</strong> Nothing in this Section 19 shall limit the rights of Users located in the European Union or the United Kingdom to bring claims before their local courts or consumer protection authorities pursuant to applicable mandatory consumer protection laws.
                      </p>
                    </div>
                  </div>
                </SubSection>
              </div>
            </CollapsibleSection>

            {/* Section 20: General Provisions */}
            <CollapsibleSection id="section-20" num="20" title="General Provisions">
              <div className="space-y-6">
                <div className="bg-gray-50 dark:bg-[#141828]/50 border border-gray-200 dark:border-white/10 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#6366F1]/10 border border-[#6366F1]/20 flex-shrink-0 text-xs font-black text-[#6366F1]">
                      a
                    </span>
                    <div>
                      <p className="text-sm font-bold text-gray-900 dark:text-white mb-2">Entire Agreement</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-3">
                        This Agreement, together with the Privacy Policy and any supplemental policies incorporated by reference, constitutes the entire agreement between the parties with respect to the subject matter hereof and supersedes all prior and contemporaneous agreements, representations, negotiations, and understandings of any kind, whether written or oral.
                      </p>
                      <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mt-3">
                        <p className="text-sm text-amber-900 dark:text-amber-200 leading-relaxed">
                          <strong className="font-bold">Your Acceptance:</strong> Your continued use of the Service following the effective date constitutes your acceptance of the amended Agreement. If you do not accept the amendment, you must discontinue use of the Service and, if applicable, cancel your subscription prior to the effective date.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-[#141828]/50 border border-gray-200 dark:border-white/10 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#6366F1]/10 border border-[#6366F1]/20 flex-shrink-0 text-xs font-black text-[#6366F1]">
                      b
                    </span>
                    <div>
                      <p className="text-sm font-bold text-gray-900 dark:text-white mb-2">Amendments</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-3">
                        JobOS reserves the right to amend this Agreement at any time. Material amendments will be communicated via email to the address associated with your account and by posting a notice within the Service not less than thirty (30) days prior to the effective date of such amendment.
                      </p>
                      <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                        <p className="text-sm text-amber-900 dark:text-amber-200 leading-relaxed">
                          <strong className="font-bold">Your Acceptance:</strong> Your continued use of the Service following the effective date constitutes your acceptance of the amended Agreement. If you do not accept the amendment, you must discontinue use of the Service and, if applicable, cancel your subscription prior to the effective date.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-[#141828]/50 border border-gray-200 dark:border-white/10 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#6366F1]/10 border border-[#6366F1]/20 flex-shrink-0 text-xs font-black text-[#6366F1]">
                      c
                    </span>
                    <div>
                      <p className="text-sm font-bold text-gray-900 dark:text-white mb-2">Severability</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                        If any provision of this Agreement is held by a court of competent jurisdiction to be invalid, illegal, or unenforceable, such provision shall be modified to the minimum extent necessary to make it enforceable, and the remaining provisions of this Agreement shall continue in full force and effect.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-[#141828]/50 border border-gray-200 dark:border-white/10 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#6366F1]/10 border border-[#6366F1]/20 flex-shrink-0 text-xs font-black text-[#6366F1]">
                      d
                    </span>
                    <div>
                      <p className="text-sm font-bold text-gray-900 dark:text-white mb-2">Waiver</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                        The failure of either party to enforce any provision of this Agreement at any time shall not constitute a waiver of that party's right to enforce such provision or any other provision at any future time.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-[#141828]/50 border border-gray-200 dark:border-white/10 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#6366F1]/10 border border-[#6366F1]/20 flex-shrink-0 text-xs font-black text-[#6366F1]">
                      e
                    </span>
                    <div>
                      <p className="text-sm font-bold text-gray-900 dark:text-white mb-2">Assignment</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-3">
                        You may not assign, delegate, or transfer your rights or obligations under this Agreement to any third party without the prior written consent of JobOS.
                      </p>
                      <div className="bg-[#6366F1]/5 dark:bg-[#6366F1]/10 border border-[#6366F1]/20 rounded-lg p-4">
                        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                          <strong className="text-gray-900 dark:text-white">JobOS Assignment Rights:</strong> JobOS may assign this Agreement or any of its rights or obligations hereunder, in whole or in part, without restriction and without notice to you, including in connection with a merger, acquisition, corporate restructuring, or sale of all or substantially all of its assets.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-[#141828]/50 border border-gray-200 dark:border-white/10 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#6366F1]/10 border border-[#6366F1]/20 flex-shrink-0 text-xs font-black text-[#6366F1]">
                      f
                    </span>
                    <div>
                      <p className="text-sm font-bold text-gray-900 dark:text-white mb-2">Force Majeure</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-3">
                        JobOS shall not be liable for any delay or failure to perform its obligations under this Agreement to the extent such delay or failure is caused by circumstances beyond its reasonable control.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {['Acts of God', 'Natural Disasters', 'Pandemic', 'War', 'Terrorism', 'Government Action', 'Power Outages', 'Third-Party Infrastructure Failures'].map((item) => (
                          <span key={item} className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-500/5 border border-amber-500/20 rounded-full text-xs text-amber-700 dark:text-amber-400 font-medium">
                            <i className="ri-alert-line text-[10px]"></i>
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-[#141828]/50 border border-gray-200 dark:border-white/10 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#6366F1]/10 border border-[#6366F1]/20 flex-shrink-0 text-xs font-black text-[#6366F1]">
                      g
                    </span>
                    <div>
                      <p className="text-sm font-bold text-gray-900 dark:text-white mb-2">Language</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-3">
                        This Agreement is executed in the English language. Any translations provided are for convenience only.
                      </p>
                      <div className="bg-gray-100 dark:bg-[#0D1117] border border-gray-200 dark:border-[#2D3148] rounded-lg p-4">
                        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                          <strong className="font-bold">Governing Version:</strong> In the event of any conflict or inconsistency between the English version and any translation, the English version shall govern.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-[#141828]/50 border border-gray-200 dark:border-white/10 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#6366F1]/10 border border-[#6366F1]/20 flex-shrink-0 text-xs font-black text-[#6366F1]">
                      h
                    </span>
                    <div>
                      <p className="text-sm font-bold text-gray-900 dark:text-white mb-2">No Agency</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                        Nothing in this Agreement shall be construed to create a partnership, joint venture, agency, franchise, employment, or fiduciary relationship between the parties.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CollapsibleSection>

            {/* Section 21: Contact Information */}
            <CollapsibleSection id="section-21" num="21" title="Contact Information">
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                All notices, requests, or communications to JobOS under this Agreement shall be directed to the appropriate contact below:
              </p>

              <div className="overflow-x-auto rounded-2xl border border-gray-200 dark:border-gray-800 mb-8">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-[#0F1629]">
                      <th className="px-5 py-3.5 text-left text-xs font-black uppercase tracking-widest text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-800">Purpose</th>
                      <th className="px-5 py-3.5 text-left text-xs font-black uppercase tracking-widest text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-800">Contact</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { purpose: 'General & Contractual Matters', email: 'hello@jobos.dev', icon: 'ri-file-text-line' },
                      { purpose: 'Privacy & Data Requests', email: 'privacy@jobos.dev', icon: 'ri-shield-check-line' },
                      { purpose: 'Technical Support', email: 'support@jobos.dev', icon: 'ri-customer-service-2-line' },
                      { purpose: 'Affiliate Programme', email: 'hello@jobos.dev', icon: 'ri-links-line' },
                    ].map((row, i) => (
                      <tr key={row.purpose} className={i % 2 === 0 ? 'bg-white dark:bg-[#0A0E1A]' : 'bg-gray-50/50 dark:bg-[#0F1629]/50'}>
                        <td className="px-5 py-4 border-b border-gray-100 dark:border-gray-800">
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#6366F1]/10 border border-[#6366F1]/20 flex-shrink-0">
                              <i className={`${row.icon} text-[#6366F1] text-sm`}></i>
                            </div>
                            <span className="font-semibold text-gray-900 dark:text-white group-hover:text-[#6366F1] transition-colors">{row.purpose}</span>
                          </div>
                        </td>
                        <td className="px-5 py-4 border-b border-gray-100 dark:border-gray-800">
                          <a
                            href={`mailto:${row.email}`}
                            className="inline-flex items-center gap-1.5 text-[#6366F1] hover:underline font-medium text-sm"
                          >
                            <i className="ri-mail-line text-xs"></i>
                            {row.email}
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="/privacy"
                  className="flex-1 flex items-center gap-3 p-5 rounded-xl bg-[#6366F1]/5 dark:bg-[#6366F1]/10 border border-[#6366F1]/20 hover:bg-[#6366F1]/10 dark:hover:bg-[#6366F1]/15 transition-colors group cursor-pointer"
                >
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#6366F1]/10 border border-[#6366F1]/20 flex-shrink-0">
                    <i className="ri-shield-check-line text-[#6366F1] text-lg"></i>
                  </div>
                  <div>
                    <div className="text-sm font-bold text-gray-900 dark:text-white group-hover:text-[#6366F1] transition-colors">Privacy Policy</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">jobos.dev/privacy</div>
                  </div>
                  <i className="ri-arrow-right-line text-[#6366F1] ml-auto opacity-0 group-hover:opacity-100 transition-opacity"></i>
                </a>

                <a
                  href="https://jobos.dev"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center gap-3 p-5 rounded-xl bg-gray-50 dark:bg-[#0F1629] border border-gray-200 dark:border-gray-800 hover:border-[#6366F1]/40 transition-colors group cursor-pointer"
                >
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800 flex-shrink-0">
                    <i className="ri-global-line text-gray-500 dark:text-gray-400 text-lg"></i>
                  </div>
                  <div>
                    <div className="text-sm font-bold text-gray-900 dark:text-white group-hover:text-[#6366F1] transition-colors">JobOS Website</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">jobos.dev</div>
                  </div>
                  <i className="ri-external-link-line text-gray-400 ml-auto opacity-0 group-hover:opacity-100 transition-opacity"></i>
                </a>
              </div>

              <div className="mt-6 p-5 rounded-xl bg-gray-50 dark:bg-[#141828]/50 border border-gray-200 dark:border-white/10 flex items-center justify-between flex-wrap gap-4">
                <p className="text-xs text-gray-400">
                  <strong className="text-gray-600 dark:text-gray-300">JobOS by TechFraser Inc.</strong> · jobos.dev · &copy; 2026
                </p>
                <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                  Terms of Service · Effective March 5, 2026
                </span>
              </div>
            </CollapsibleSection>

            {/* Footer note */}
            <div className="pt-8 border-t border-gray-100 dark:border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-xs text-gray-400">
                <strong className="text-gray-600 dark:text-gray-300">JobOS by TechFraser Inc.</strong> — Terms of Service — Effective March 5, 2026
              </p>
              <div className="flex items-center gap-4">
                <a
                  href="mailto:hello@jobos.dev"
                  className="inline-flex items-center gap-2 text-xs text-[#6366F1] hover:underline font-medium whitespace-nowrap"
                >
                  <i className="ri-mail-line"></i>
                  hello@jobos.dev
                </a>
                <a
                  href="/privacy"
                  className="inline-flex items-center gap-2 text-xs text-[#6366F1] hover:underline font-medium whitespace-nowrap"
                >
                  <i className="ri-shield-check-line"></i>
                  Privacy Policy
                </a>
              </div>
            </div>

            {/* See also cross-link */}
            <div className="mt-10">
              <p className="text-xs font-black uppercase tracking-[0.2em] text-gray-400 dark:text-gray-500 font-mono mb-4">See Also</p>
              <a
                href="/privacy"
                className="flex items-center gap-5 p-5 rounded-2xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-[#141828]/40 hover:border-[#6366F1]/40 hover:bg-[#6366F1]/5 dark:hover:bg-[#6366F1]/10 transition-all group cursor-pointer"
              >
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#6366F1]/10 border border-[#6366F1]/20 flex-shrink-0">
                  <i className="ri-shield-check-line text-[#6366F1] text-xl"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-gray-900 dark:text-white group-hover:text-[#6366F1] transition-colors">
                    Privacy Policy
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Learn how we collect, use, and protect your personal data — including your rights under Canadian privacy law.
                  </div>
                </div>
                <i className="ri-arrow-right-line text-[#6366F1] text-lg opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"></i>
              </a>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}